import React, { useState, useEffect } from "react";
import LineChartComponent from "./common/LineChart.js";
import TabPanel from "./common/TabPanel";
import ParameterTable from "./common/ParameterTable";
import { parameterGroups } from "./OtherParameters"; // Import from OtherParameters

// Define tab configuration
const touTabs = [
  { id: "depot", label: "Depot Charging" },
  { id: "public", label: "Public Charging" },
  { id: "diesel", label: "Diesel" }
];

// Add initialEnergyChargeData here
const initialEnergyChargeData = [
  { x: 0, y: 0.17133 },
  { x: 1, y: 0.17133 },
  { x: 2, y: 0.17133 },
  { x: 3, y: 0.17133 },
  { x: 4, y: 0.17133 },
  { x: 5, y: 0.17133 },
  { x: 6, y: 0.17133 },
  { x: 7, y: 0.17133 },
  { x: 8, y: 0.17133 },
  { x: 9, y: 0.17133 },
  { x: 10, y: 0.17133 },
  { x: 11, y: 0.17133 },
  { x: 12, y: 0.17133 },
  { x: 13, y: 0.17133 },
  { x: 14, y: 0.17133 },
  { x: 15, y: 0.17133 },
  { x: 16, y: 0.46034 },
  { x: 17, y: 0.46034 },
  { x: 18, y: 0.46034 },
  { x: 19, y: 0.46034 },
  { x: 20, y: 0.46034 },
  { x: 21, y: 0.17133 },
  { x: 22, y: 0.17133 },
  { x: 23, y: 0.17133 },
];

// Add TOU preprocessing function
export const expandEnergyChargeData = (data) => {
  // Maps 1-hour increment to 15-minute increment
  return data.reduce((result, { x, y }) => {
    for (let i = 0; i < 4; i++) {
      result[x * 4 + i] = y; // Add key-value pair to the result
    }
    return result;
  }, {});
};

const EnergyRate = ({ 
  energyChargeData, 
  setEnergyChargeData, 
  otherParameters,
  setOtherParameters,
  nextStep, 
  prevStep 
}) => {
  const [activeTab, setActiveTab] = useState("depot"); // Default active tab

  // Initialize energy charge data if empty
  useEffect(() => {
    if (energyChargeData.length === 0) {
      setEnergyChargeData(initialEnergyChargeData);
    }
  }, [energyChargeData, setEnergyChargeData]);

  // Handle parameter changes
  const handleInputChange = (id, value) => {
    setOtherParameters(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  // Format currency values with decimal places
  const formatCurrency = (value) => {
    return parseFloat(value).toFixed(4);
  };

  return (
    <div className="step-container">
      <TabPanel 
        tabs={touTabs} 
        activeTab={activeTab} 
        onTabChange={setActiveTab}
      >
        {/* Tab 1: Depot Charging */}
        <div className="tab-panel-section">
          <h3>Energy Charges</h3>
          <p style={{ textAlign: 'justify' }}>These are the time-of-use (TOU) rates your depot pays to the utility. The example shows SCE's EV-9 Schedule for 2kV-50kV customers. You can download the CSV template, make adjustments, and upload it again.</p>

          <div className="tou-chart-container">
            <LineChartComponent
              xLabel="Time"
              yLabel="Energy Charge ($/kWh)"
              initialData={energyChargeData.length > 0 ? energyChargeData : initialEnergyChargeData}
              onDataUpdate={setEnergyChargeData} 
            />
          </div>

          <div className="parameters-section" style={{ marginTop: '20px', marginBottom: '20px' }}>
            <h3>Demand Charges</h3>
            <p style={{ textAlign: 'justify' }}>This is part of your electricity bill based on your peak load. Utilities charge this fee to most commercial and industrial users to cover additional power infrastructure costs. Enter the monthly power demand charges applied by your utility (Note that SCE's EV-9 Schedule currently has no demand charges).</p>
            <ParameterTable 
              parameters={parameterGroups.depotCharging.inputs}
              values={otherParameters}
              onValueChange={handleInputChange}
              formatValue={formatCurrency}
            />
          </div>
        </div>
        
        {/* Tab 2: Public Charging */}
        <div className="tab-panel-section">
          <h3>Public Charging Settings</h3>
          <p style={{ textAlign: 'justify' }}>Most third-party public charging stations charge additional fees on top of what they pay to utilities, typically in $/kWh. We recognize that each charging station may have its own pricing policy, and we are actively working to bring more pricing options to this platform. Stay tuned!</p>
          
          <div className="parameters-section">
            <ParameterTable 
              parameters={parameterGroups.publicCharging.inputs}
              values={otherParameters}
              onValueChange={handleInputChange}
              formatValue={formatCurrency}
            />
          </div>
        </div>
        
        {/* Tab 3: Diesel */}
        <div className="tab-panel-section">
          <h3>Diesel Fuel Settings</h3>
          <p style={{ textAlign: 'justify' }}>Configure the diesel fuel costs for comparison purposes.</p>
          
          <div className="parameters-section">
            <ParameterTable 
              parameters={parameterGroups.diesel.inputs}
              values={otherParameters}
              onValueChange={handleInputChange}
              formatValue={formatCurrency}
            />
          </div>
        </div>
      </TabPanel>
    </div>
  );
};

export default EnergyRate; 